/**
 * A surface's material state in the two passes its geometry instance's fade can draw it in:
 * unfaded, and the alpha pass a fade below the threshold forces (`godot/rendering/fadeAlpha.ts`). The
 * scene cull picks one of the two materials per render (`swappedMaterials.ts`).
 */
import { surfaceAlphaProps } from './surfaceAlphaPatch';
import { DRAWN_OPAQUE_PREPASS, FADED_OPAQUE_PREPASS, NO_OPAQUE_PREPASS, } from './opaquePrepass';
/**
 * The surface's alpha state in each pass, and the props its blend then needs. The alpha-pass
 * `opacity` is the unfaded one: the cull multiplies the fade alpha in, as Godot's shader starts
 * ALPHA from it, which also lowers what a cut keeps.
 */
export function surfaceFadeVariants(source, surface) {
    const { opacity, blending } = surface;
    const unfaded = { opacity, transparent: surface.transparent, depthWrite: surface.depthWrite };
    const alphaPass = { opacity, transparent: true, depthWrite: surface.alphaPassDepthWrite };
    return {
        unfaded: { ...unfaded, ...surfaceAlphaProps(source, { ...unfaded, blending }) },
        alphaPass: { ...alphaPass, ...surfaceAlphaProps(source, { ...alphaPass, blending }) },
    };
}
/**
 * The blend of each pass of a Sprite3D or Label3D surface, whose `get_material_for_2d` material
 * keeps DEPTH_DRAW_OPAQUE_ONLY, so the alpha pass writes no depth. Both draw MIX, three's default
 * blending. A Label3D run takes its opacity from its tint. A disabled depth test files the unfaded
 * surface in the alpha pass too (`scene_shader_forward_clustered.h:279-287`).
 */
export function cutBlends(cut, depthTest) {
    const { alphaTest, alphaHash } = cut;
    const alphaPass = { transparent: true, depthWrite: false };
    const unfaded = depthTest ? { transparent: cut.blended, depthWrite: cut.depthWrite } : alphaPass;
    return {
        unfaded: { ...unfaded, ...surfaceAlphaProps(cut, unfaded), alphaTest, alphaHash },
        alphaPass: { ...alphaPass, ...surfaceAlphaProps(cut, alphaPass), alphaTest, alphaHash },
    };
}
const NO_OPAQUE_PREPASSES = {
    unfaded: NO_OPAQUE_PREPASS,
    alphaPass: NO_OPAQUE_PREPASS,
};
const CUT_OPAQUE_PREPASSES = {
    unfaded: DRAWN_OPAQUE_PREPASS,
    alphaPass: FADED_OPAQUE_PREPASS,
};
/**
 * The opaque prepass of each pass of a Sprite3D or Label3D surface. A disabled depth test leaves
 * the surface none (`scene_shader_forward_clustered.h:288-292`).
 */
export function cutOpaquePrepasses(cut, depthTest) {
    return cut.depthPrepass && depthTest ? CUT_OPAQUE_PREPASSES : NO_OPAQUE_PREPASSES;
}
//# sourceMappingURL=fadeVariants.js.map